import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Sparkles, ClipboardCheck, ArrowRight, History } from 'lucide-react';
import { getEnabledModes } from '../../modes/modeRegistry';
import { ModeCard } from './ModeCard';
import { RecommendedModes } from './RecommendedModes';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { PageHeader } from '../shared/PageHeader';
import { AssessmentHistoryEntry } from '../assessment/types';
interface LearningLabPageProps {
  onStartAssessment?: () => void;
  onStartMode?: (modeId: string) => void;
  assessmentHistory?: AssessmentHistoryEntry[];
}
export function LearningLabPage({
  onStartAssessment,
  onStartMode,
  assessmentHistory = []
}: LearningLabPageProps) {
  const enabledModes = getEnabledModes();
  const handleModeClick = (modeId: string) => {
    if (onStartMode) {
      onStartMode(modeId);
    }
  };
  const lastAssessment = assessmentHistory.length > 0 ? assessmentHistory[0] : null;
  return <div className="max-w-5xl mx-auto pb-12 space-y-8" data-id="element-3970">
      {/* Page Header */}
      <PageHeader icon={Lightbulb} title="Oefenlab" subtitle="Ontdek nieuwe manieren om te oefenen" accentColor="bg-violet-100 text-violet-600" data-id="element-3971" />

      {/* Assessment Card (Toets) */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} data-id="element-3972">
        <div className="flex items-center gap-2 mb-4" data-id="element-3973">
          <ClipboardCheck className="w-5 h-5 text-indigo-600" data-id="element-3974" />
          <h2 className="text-xl font-bold text-slate-900" data-id="element-3975">Toetsen</h2>
        </div>

        <Card className="overflow-hidden border-2 border-indigo-100 hover:border-indigo-200 transition-all cursor-pointer" onClick={onStartAssessment} data-id="element-3976">
          <div className="flex flex-col sm:flex-row" data-id="element-3977">
            <div className="bg-indigo-600 p-6 sm:p-8 flex flex-col justify-center items-center text-white sm:w-1/3" data-id="element-3978">
              <ClipboardCheck className="w-12 h-12 mb-3" data-id="element-3979" />
              <h3 className="text-xl font-black text-center" data-id="element-3980">Oefentoets</h3>
              <div className="mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-bold" data-id="element-3981">
                30 vragen · ~15 min
              </div>
            </div>

            <div className="p-6 sm:p-8 sm:w-2/3 flex flex-col justify-center" data-id="element-3982">
              <h4 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3983">
                Ontdek je niveau
              </h4>
              <p className="text-slate-600 mb-5 leading-relaxed" data-id="element-3984">
                Doe een volledige oefentoets met vragen uit alle vakken. Je
                krijgt na afloop een uitgebreid rapport met je geschatte niveau
                en persoonlijke tips.
              </p>

              <div className="flex items-center justify-between" data-id="element-3985">
                <div className="text-sm" data-id="element-3986">
                  {lastAssessment ? <>
                      <span className="text-slate-500" data-id="element-3987">Vorige toets:</span>
                      <span className="font-bold text-indigo-600 ml-2" data-id="element-3988">
                        {lastAssessment.score}% (
                        {new Date(lastAssessment.completedAt).toLocaleDateString('nl-NL')}
                        )
                      </span>
                    </> : <span className="text-slate-500 italic" data-id="element-3989">
                      Nog geen toetsen gemaakt
                    </span>}
                </div>

                <Button onClick={e => {
                e.stopPropagation();
                onStartAssessment?.();
              }} variant="primary" size="lg" data-id="element-3990">
                  Start Toets <ArrowRight className="w-5 h-5 ml-2" data-id="element-3991" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Recommended Modes Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-3992">
        <RecommendedModes onModeClick={handleModeClick} data-id="element-3993" />
      </motion.div>

      {/* All Modes Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-3994">
        <div className="flex items-center gap-2 mb-4" data-id="element-3995">
          <Sparkles className="w-5 h-5 text-violet-600 fill-violet-600" data-id="element-3996" />
          <h2 className="text-xl font-bold text-slate-900" data-id="element-3997">Trainingsmodi</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3998">
          {enabledModes.map((mode, index) => <motion.div key={mode.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35 + index * 0.05
        }} data-id="element-3999">
              <ModeCard mode={mode} onClick={() => handleModeClick(mode.id)} data-id="element-4000" />
            </motion.div>)}
        </div>
      </motion.div>

      {/* Assessment History Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5
    }} data-id="element-4001">
        <div className="flex items-center gap-2 mb-4" data-id="element-4002">
          <History className="w-5 h-5 text-slate-400" data-id="element-4003" />
          <h2 className="text-xl font-bold text-slate-900" data-id="element-4004">
            Toetsgeschiedenis
          </h2>
        </div>

        {assessmentHistory.length > 0 ? <div className="space-y-3" data-id="element-4005">
            {assessmentHistory.map(entry => <Card key={entry.attemptId} className="p-4 flex items-center justify-between" data-id="element-4006">
                <div className="flex items-center gap-4" data-id="element-4007">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white ${entry.score >= 80 ? 'bg-emerald-500' : entry.score >= 60 ? 'bg-blue-500' : 'bg-amber-500'}`} data-id="element-4008">
                    {entry.score}%
                  </div>
                  <div data-id="element-4009">
                    <div className="font-bold text-slate-900" data-id="element-4010">
                      Oefentoets Groep {entry.selectedGroup}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-4011">
                      {new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-4012">
                  <div className="font-medium text-slate-700" data-id="element-4013">
                    {entry.totalQuestions} vragen
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-4014">
                    {Math.round(entry.totalTimeSeconds / 60)} min
                  </div>
                </div>
              </Card>)}
          </div> : <Card className="p-8 text-center border-dashed border-2 border-slate-200 bg-slate-50/50 rounded-2xl" data-id="element-4015">
            <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-4016">
              <History className="w-6 h-6" data-id="element-4017" />
            </div>
            <h3 className="text-base font-bold text-slate-700 mb-1" data-id="element-4018">
              Nog geen toetsen gemaakt
            </h3>
            <p className="text-sm text-slate-500" data-id="element-4019">
              Doe je eerste oefentoets om je resultaten hier te zien.
            </p>
          </Card>}
      </motion.div>
    </div>;
}